import type { 
  ArbitrageOpportunity, 
  OpportunitySnapshot, 
  OpportunityChange,
  OpportunityWithSnapshot 
} from '../types/opportunity';
import { computeDisplayProperties } from '../types/opportunity';

/**
 * Compare current opportunity with its snapshot to detect changes
 */
export function compareOpportunityWithSnapshot(
  current: ArbitrageOpportunity | undefined,
  snapshot: OpportunitySnapshot
): OpportunityChange {
  const timeSinceSnapshot = Date.now() - snapshot.snapshotTimestamp;
  
  // If opportunity no longer exists
  if (!current) {
    return {
      buyPriceChange: 0,
      sellPriceChange: 0,
      buyVolumeChange: 0,
      sellVolumeChange: 0,
      maxVolumeChange: 0,
      profitRatioChange: 0,
      isDeleted: true,
      timeSinceSnapshot
    };
  }
  
  // Compute display properties for both
  const currentWithProps = computeDisplayProperties(current);
  const snapshotWithProps = computeDisplayProperties(snapshot.opportunity);
  
  // Calculate price changes (percentage)
  const buyPriceChange = calculatePercentageChange(
    snapshotWithProps.buy_price || 0,
    currentWithProps.buy_price || 0
  );
  
  const sellPriceChange = calculatePercentageChange(
    snapshotWithProps.sell_price || 0,
    currentWithProps.sell_price || 0
  );
  
  // Calculate volume changes (absolute)
  const snapshotBuyVolume = snapshot.opportunity.buy_levels.reduce((sum, level) => sum + level.volume, 0);
  const currentBuyVolume = current.buy_levels.reduce((sum, level) => sum + level.volume, 0);
  const buyVolumeChange = currentBuyVolume - snapshotBuyVolume;
  
  const snapshotSellVolume = snapshot.opportunity.sell_levels.reduce((sum, level) => sum + level.volume, 0);
  const currentSellVolume = current.sell_levels.reduce((sum, level) => sum + level.volume, 0);
  const sellVolumeChange = currentSellVolume - snapshotSellVolume;
  
  const maxVolumeChange = (currentWithProps.max_volume || 0) - (snapshotWithProps.max_volume || 0);
  
  // Calculate profit ratio change (in percentage points)
  const snapshotProfit = ((snapshotWithProps.profit_ratio || 1) - 1) * 100;
  const currentProfit = ((currentWithProps.profit_ratio || 1) - 1) * 100;
  const profitRatioChange = currentProfit - snapshotProfit;
  
  return {
    buyPriceChange,
    sellPriceChange,
    buyVolumeChange,
    sellVolumeChange,
    maxVolumeChange,
    profitRatioChange,
    isDeleted: false,
    timeSinceSnapshot
  };
}

/**
 * Calculate percentage change between two values
 */
function calculatePercentageChange(oldValue: number, newValue: number): number {
  if (oldValue === 0) return 0;
  return ((newValue - oldValue) / oldValue) * 100;
}

/**
 * Format change percentage for display
 */
export function formatChangePercentage(change: number): string {
  if (Math.abs(change) < 0.01) return '0.00%';
  const sign = change > 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
}

/**
 * Format volume change for display
 */
export function formatVolumeChange(change: number): string {
  if (Math.abs(change) < 0.001) return '0.000';
  const sign = change > 0 ? '+' : '';
  return `${sign}${change.toFixed(3)}`;
}

/**
 * Format profit ratio change for display (in percentage points)
 */
export function formatProfitChange(change: number): string {
  if (Math.abs(change) < 0.01) return '0.00pp';
  const sign = change > 0 ? '+' : '-';
  return `${sign}${Math.abs(change).toFixed(2)}pp`;
}

/**
 * Get color for change indicator
 */
export function getChangeColor(change: number, inverse: boolean = false): string {
  if (Math.abs(change) < 0.01) return 'rgba(255, 255, 255, 0.5)'; // No significant change
  
  if (inverse) {
    // For buy price, lower is better
    return change < 0 ? '#4ade80' : '#f87171'; // Green for decrease, red for increase
  } else {
    // For sell price and profit, higher is better
    return change > 0 ? '#4ade80' : '#f87171'; // Green for increase, red for decrease
  }
}

/**
 * Determine if a change is significant
 */
export function isSignificantChange(change: number, threshold: number = 1): boolean {
  return Math.abs(change) >= threshold;
}

/**
 * Format elapsed time since snapshot
 */
export function formatTimeSinceSnapshot(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ago`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s ago`;
  } else {
    return `${seconds}s ago`;
  }
}

/**
 * Merge opportunity with its snapshot data for display
 */
export function mergeOpportunityWithSnapshot(
  opportunity: ArbitrageOpportunity | undefined,
  snapshot: OpportunitySnapshot | undefined
): OpportunityWithSnapshot | null {
  // If we have a snapshot but no current opportunity, return the snapshot as deleted
  if (snapshot && !opportunity) {
    const changes = compareOpportunityWithSnapshot(undefined, snapshot);
    return {
      ...snapshot.opportunity,
      isStarred: true,
      snapshot,
      changes
    };
  }
  
  // If we have both, merge them
  if (opportunity && snapshot) {
    const changes = compareOpportunityWithSnapshot(opportunity, snapshot);
    return {
      ...opportunity,
      isStarred: true,
      snapshot,
      changes
    };
  }
  
  // If we only have the opportunity (no snapshot), return it as-is
  if (opportunity) {
    return {
      ...opportunity,
      isStarred: false
    };
  }
  
  return null;
}